import { type FormEvent, type ReactNode, useId, useState } from 'react'
import { toast } from 'react-hot-toast'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { trpc } from '@/lib/trpc'
import { errorCode } from '../shared/trpc-error'
import {
  type ProfileFieldName,
  type ProfileFormErrors,
  type ProfileFormPayload,
  type ProfileFormSource,
  type ProfileFormValues,
  toFormValues,
  validateProfileForm,
} from './profile-editor-form'

const COUNT_FIELDS: {
  name: ProfileFieldName
  label: string
  hint: string
}[] = [
  {
    name: 'clientElo',
    label: 'Client ELO',
    hint: 'Drives computed time estimates. Blank leaves them on stored values.',
  },
  {
    name: 'optimalEloBand',
    label: 'Optimal ELO band',
    hint: 'Half-width around the client ELO. Library shading needs both fields.',
  },
  { name: 'targetRuns', label: 'Target runs', hint: 'Blank to clear.' },
  { name: 'targetSubmits', label: 'Target submits', hint: 'Blank to clear.' },
]

export interface ProfileEditorDialogViewProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Read on mount only — the container remounts via `key` to reseed. */
  initialValues: ProfileFormValues
  onSubmit: (payload: ProfileFormPayload) => void
  saving: boolean
  /** Set after a losing optimistic-concurrency race; the form stays open. */
  conflictMessage: string | null
}

/**
 * Below a field's input: the validation error for that field, if any.
 * Hoisted to module scope (rather than defined inside the render body of
 * `ProfileEditorDialogView`) so it keeps a stable type identity across
 * renders instead of being recreated — and thus fully remounted — every time.
 */
function FieldError(props: {
  errors: ProfileFormErrors
  name: ProfileFieldName
  errorId: string
}): ReactNode {
  const message = props.errors[props.name]
  if (!message) return null
  return (
    <p
      id={props.errorId}
      role="alert"
      className="text-[10.5px] text-(--cd-red)"
    >
      {message}
    </p>
  )
}

/**
 * Presentational profile editor (LG-340). Owns only its input strings; the
 * container owns the write. Every field the `profile.update` mutation replaces
 * is rendered, because it is a full replace — an ELO-only form would still
 * have to round-trip the other four, and hiding them would let the editor
 * silently rewrite values the coach never saw.
 */
export function ProfileEditorDialogView(
  props: ProfileEditorDialogViewProps,
): ReactNode {
  const [values, setValues] = useState<ProfileFormValues>(props.initialValues)
  const [errors, setErrors] = useState<ProfileFormErrors>({})
  const fieldId = useId()

  const idFor = (name: ProfileFieldName): string => `${fieldId}-${name}`
  const errorIdFor = (name: ProfileFieldName): string =>
    `${fieldId}-${name}-error`

  const setField = (name: ProfileFieldName, value: string): void => {
    setValues((current) => ({ ...current, [name]: value }))
  }

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault()
    const result = validateProfileForm(values)
    if (!result.ok) {
      setErrors(result.errors)
      return
    }
    setErrors({})
    props.onSubmit(result.payload)
  }

  const fieldProps = (name: ProfileFieldName) => ({
    id: idFor(name),
    value: values[name],
    'aria-invalid': errors[name] ? (true as const) : undefined,
    'aria-describedby': errors[name] ? errorIdFor(name) : undefined,
    className:
      'rounded border border-(--cd-border-2) bg-(--cd-bg) px-2 py-1 text-[12px] text-(--cd-text) aria-[invalid=true]:border-(--cd-red)',
  })

  return (
    <Dialog onOpenChange={props.onOpenChange} open={props.open}>
      <DialogContent
        className="coaching-dashboard border-(--cd-border-4) bg-(--cd-panel) px-[18px] py-4 text-(--cd-text) shadow-[0_24px_60px_rgba(0,0,0,.6)] sm:max-w-[520px]"
        onEscapeKeyDown={(event) => {
          if (props.saving) event.preventDefault()
        }}
        onInteractOutside={(event) => {
          if (props.saving) event.preventDefault()
        }}
        showCloseButton={!props.saving}
      >
        <DialogHeader className="text-left">
          <DialogTitle className="text-[13px] font-bold">
            Edit engagement profile
          </DialogTitle>
          <DialogDescription className="text-[11px] text-(--cd-text-faint)">
            Saving replaces the whole profile. Leave a number blank to clear it.
          </DialogDescription>
        </DialogHeader>

        {props.conflictMessage ? (
          <p
            role="alert"
            className="rounded border border-(--cd-amber) px-2 py-1.5 text-[11px] text-(--cd-amber)"
          >
            {props.conflictMessage}
          </p>
        ) : null}

        <form className="flex flex-col gap-3" onSubmit={handleSubmit}>
          <label className="flex flex-col gap-1" htmlFor={idFor('goal')}>
            <span className="text-[10.5px] text-(--cd-text-faint)">Goal</span>
            <input
              {...fieldProps('goal')}
              onChange={(event) => setField('goal', event.currentTarget.value)}
              type="text"
            />
          </label>
          <FieldError
            errors={errors}
            name="goal"
            errorId={errorIdFor('goal')}
          />

          <label className="flex flex-col gap-1" htmlFor={idFor('targetDate')}>
            <span className="text-[10.5px] text-(--cd-text-faint)">
              Target date
            </span>
            <input
              {...fieldProps('targetDate')}
              onChange={(event) =>
                setField('targetDate', event.currentTarget.value)
              }
              type="date"
            />
          </label>
          <FieldError
            errors={errors}
            name="targetDate"
            errorId={errorIdFor('targetDate')}
          />

          {COUNT_FIELDS.map((field) => (
            <div className="flex flex-col gap-1" key={field.name}>
              <label
                className="flex flex-col gap-1"
                htmlFor={idFor(field.name)}
              >
                <span className="text-[10.5px] text-(--cd-text-faint)">
                  {field.label}
                </span>
                <input
                  {...fieldProps(field.name)}
                  // `type="text"` with inputMode, not `type="number"`: a number
                  // input reports '' for '-5' and '1e3' in some browsers, which
                  // would read as "cleared to null" instead of "invalid".
                  inputMode="numeric"
                  onChange={(event) =>
                    setField(field.name, event.currentTarget.value)
                  }
                  type="text"
                />
              </label>
              <span className="text-[10px] text-(--cd-text-ghost)">
                {field.hint}
              </span>
              <FieldError
                errors={errors}
                name={field.name}
                errorId={errorIdFor(field.name)}
              />
            </div>
          ))}

          <DialogFooter className="mt-1 gap-2">
            <button
              className="rounded-md px-3 py-1.5 text-xs font-medium text-(--cd-text-muted) disabled:opacity-50"
              disabled={props.saving}
              onClick={() => props.onOpenChange(false)}
              type="button"
            >
              Cancel
            </button>
            <button
              className="rounded-md bg-(--cd-tab-active) px-3 py-1.5 text-xs font-medium text-(--cd-text) disabled:opacity-50"
              disabled={props.saving}
              type="submit"
            >
              {props.saving ? 'Saving…' : 'Save profile'}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

const CONFLICT_MESSAGE =
  'This engagement changed in another session. The latest values have been loaded — review them and save again.'

export interface ProfileEditorEngagement extends ProfileFormSource {
  id: string
  clientId: string
  version: number
}

/**
 * Container: persists the profile through `profile.update` and refreshes the
 * caches that consume the ELO fields. On CONFLICT it refetches and reseeds but
 * never replays the write — the coach re-reads the winning values first, the
 * same doctrine the curriculum `mutation()` helper follows.
 */
export function ProfileEditorDialog(props: {
  open: boolean
  onOpenChange: (open: boolean) => void
  engagement: ProfileEditorEngagement
}): ReactNode {
  const { engagement } = props
  const utils = trpc.useUtils()
  const update = trpc.admin.coachingV2.profile.update.useMutation()
  const [conflictMessage, setConflictMessage] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  // Bumped after a conflict refetch to remount the view, which reads
  // `initialValues` on mount only.
  const [seed, setSeed] = useState(0)

  /**
   * Every cache that reads a profile field the mutation replaces. Both the
   * success and the CONFLICT path drop all of them: on a conflict it is the
   * *winner's* write that landed, so the caches are just as stale as they are
   * after our own — `plan.engagement` feeds tray estimates and browse-table
   * band shading, and nothing else would refresh it.
   *
   * `reviews.listWeekWork` carries the engagement's verification targets
   * (LG-637): a target is live policy that re-judges the reviews already read,
   * which only holds if changing it here refetches them. The input-less
   * invalidate covers every week section the coach has open (LG-646).
   */
  const invalidateProfileCaches = async (): Promise<void> => {
    await Promise.all([
      utils.admin.coachingV2.overview.get.invalidate({
        clientId: engagement.clientId,
      }),
      utils.admin.coachingV2.curriculum.getPlan.invalidate(),
      utils.admin.coachingV2.reviews.listWeekWork.invalidate(),
    ])
  }

  const handleSubmit = async (payload: ProfileFormPayload): Promise<void> => {
    // The write is a full replace at a fixed `expectedVersion`, so a re-entrant
    // submit would either duplicate the audit event or lose to itself. The
    // disabled submit button already blocks the implicit-submission path, but
    // the container owns the write, so the guard belongs on the write.
    if (saving) return
    setSaving(true)
    setConflictMessage(null)
    try {
      await update.mutateAsync({
        clientId: engagement.clientId,
        engagementId: engagement.id,
        expectedVersion: engagement.version,
        ...payload,
      })
      await invalidateProfileCaches()
      toast.success('Profile updated.')
      props.onOpenChange(false)
    } catch (error) {
      if (errorCode(error) === 'CONFLICT') {
        await invalidateProfileCaches()
        setConflictMessage(CONFLICT_MESSAGE)
        setSeed((current) => current + 1)
        return
      }
      toast.error(
        error instanceof Error
          ? error.message
          : 'The profile could not be saved.',
      )
    } finally {
      setSaving(false)
    }
  }

  // Reseed the form when the server's PROFILE VALUES change, not merely when
  // the engagement version does. `seed` still forces a reseed after a
  // conflict, where discarding the input is the intent.
  const profileSignature = JSON.stringify(toFormValues(engagement))

  return (
    <ProfileEditorDialogView
      conflictMessage={conflictMessage}
      initialValues={toFormValues(engagement)}
      key={`${profileSignature}-${seed}`}
      onOpenChange={props.onOpenChange}
      onSubmit={(payload) => void handleSubmit(payload)}
      open={props.open}
      saving={saving}
    />
  )
}
